import {Axios} from 'services/axios';

export const valuePackApi = {
  fetchAllValuePacksOptimized: async (options = {}) => {
    try {
      const {page = 1, limit = 100, includeFeatures = true} = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        includeFeatures: String(includeFeatures),
      });

      const response = await Axios.fetch(
        `/valuepack/getAllValuePacksOptimized?${params}`,
      );

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const responseData = response.data.data || response.data || {};

      if (!responseData.valuePacks || !Array.isArray(responseData.valuePacks)) {
        console.warn('Value packs data is not an array:', responseData);
        throw new Error('Value packs data is not in expected format');
      }

      return responseData;
    } catch (error) {
      console.error('Error fetching optimized value packs:', error);

      let errorMessage = 'Failed to fetch value packs';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  // Lean list for the admin table: only what a row shows (no tiers/features).
  fetchValuePackList: async ({page = 1, limit = 10, search = ''} = {}) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search) params.append('search', search);

      const response = await Axios.fetch(`/valuepack/list?${params}`);
      const responseData = response?.data?.data || response?.data;

      if (!responseData || !Array.isArray(responseData.valuePacks)) {
        throw new Error('Value packs data is not in expected format');
      }

      return responseData;
    } catch (error) {
      console.error('Error fetching value pack list:', error);
      throw new Error(
        error.response?.data?.message ||
          (error.request
            ? 'Network error: Unable to reach server'
            : error.message || 'Failed to fetch value packs'),
      );
    }
  },

  // One value pack with everything view/edit need (all tiers, categories, features).
  fetchValuePackDetails: async id => {
    try {
      if (!id) {
        throw new Error('Value pack ID is required');
      }

      const response = await Axios.fetch(`/valuepack/details/${id}`);
      const responseData = response?.data?.data || response?.data;
      const valuePack = responseData?.valuePack;

      if (!valuePack) {
        throw new Error('Value pack not found');
      }

      return valuePack;
    } catch (error) {
      console.error('Error fetching value pack details:', error);
      throw new Error(
        error.response?.status === 404
          ? 'Value pack not found'
          : error.response?.data?.message ||
              (error.request
                ? 'Network error: Unable to reach server'
                : error.message || 'Failed to fetch value pack'),
      );
    }
  },

  createValuePack: async valuePackData => {
    try {
      const formData = new FormData();

      // Append all basic fields
      formData.append('name', valuePackData.name || '');
      formData.append('subTitle', valuePackData.subTitle || '');
      formData.append('slug', valuePackData.slug || '');
      formData.append('shortDescription', valuePackData.shortDescription || '');
      formData.append('description', valuePackData.description || '');
      formData.append('active', valuePackData.active ? 'true' : 'false');
      formData.append('featured', valuePackData.featured ? 'true' : 'false');

      // CHANGED: durationId and countryId
      // For duration - extract ID if it's an object
      let durationValue =
        valuePackData.durationId || valuePackData.selectedDuration;
      if (
        durationValue &&
        typeof durationValue === 'object' &&
        durationValue.id
      ) {
        durationValue = durationValue.id;
      }
      formData.append('durationId', durationValue || '');

      // For country - extract ID if it's an object
      let countryValue =
        valuePackData.countryId || valuePackData.selectedCountry;
      if (countryValue && typeof countryValue === 'object' && countryValue.id) {
        countryValue = countryValue.id;
      }
      formData.append('countryId', countryValue || '');

      // Append arrays as JSON strings
      if (valuePackData.categories) {
        const categoryIds = Array.isArray(valuePackData.categories)
          ? valuePackData.categories.map(cat =>
              typeof cat === 'object' ? cat.id : cat,
            )
          : [];
        formData.append('categories', JSON.stringify(categoryIds));
      }

      if (valuePackData.tags) {
        formData.append('tags', JSON.stringify(valuePackData.tags));
      }

      // Target Industries is no longer in the form: a new pack gets ['all']
      formData.append(
        'targetIndustries',
        JSON.stringify(
          valuePackData.targetIndustries?.length
            ? valuePackData.targetIndustries
            : ['all'],
        ),
      );

      // Tags for the custom storefront filters (Settings > Dynamic Filters)
      if (valuePackData.filterValues) {
        formData.append('filterValues', JSON.stringify(valuePackData.filterValues));
      }

      // Append files
      if (valuePackData.imageFile instanceof File) {
        formData.append('image', valuePackData.imageFile);
      }

      if (valuePackData.bannerImageFile instanceof File) {
        formData.append('bannerImage', valuePackData.bannerImageFile);
      }

      // Append dates
      if (valuePackData.publishedAt) {
        formData.append('publishedAt', valuePackData.publishedAt);
      }

      if (valuePackData.expiresAt) {
        formData.append('expiresAt', valuePackData.expiresAt);
      }

      // Debug: Check FormData contents
      // for (let [key, value] of formData.entries()) {
      //   console.log(`  ${key}:`, value instanceof File ? `File: ${value.name}` : value);
      // }

      const response = await Axios.post(
        `/valuepack/createValuePack`,
        formData,
        {
          // headers: {
          'Content-Type': 'multipart/form-data',
          // },
        },
      );

      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error creating value pack:', error);

      let errorMessage = 'Failed to create value pack';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  updateValuePack: async (id, valuePackData) => {
    try {
      if (!id) {
        throw new Error('Value pack ID is required for update');
      }

      const formData = new FormData();

      // Append all basic fields
      formData.append('name', valuePackData.name || '');
      formData.append('subTitle', valuePackData.subTitle || '');
      formData.append('slug', valuePackData.slug || '');
      formData.append('shortDescription', valuePackData.shortDescription || '');
      formData.append('description', valuePackData.description || '');
      formData.append('active', valuePackData.active ? 'true' : 'false');
      formData.append('featured', valuePackData.featured ? 'true' : 'false');

      // CHANGED: durationId and countryId
      // For duration - extract ID if it's an object
      let durationValue =
        valuePackData.durationId || valuePackData.selectedDuration;
      if (
        durationValue &&
        typeof durationValue === 'object' &&
        durationValue.id
      ) {
        durationValue = durationValue.id;
      }
      formData.append('durationId', durationValue || '');

      // For country - extract ID if it's an object
      let countryValue =
        valuePackData.countryId || valuePackData.selectedCountry;
      if (countryValue && typeof countryValue === 'object' && countryValue.id) {
        countryValue = countryValue.id;
      }
      formData.append('countryId', countryValue || '');

      // Append arrays as JSON strings
      if (valuePackData.categories) {
        const categoryIds = Array.isArray(valuePackData.categories)
          ? valuePackData.categories.map(cat =>
              typeof cat === 'object' ? cat.id : cat,
            )
          : [];
        formData.append('categories', JSON.stringify(categoryIds));
      }

      if (valuePackData.tags) {
        formData.append('tags', JSON.stringify(valuePackData.tags));
      }

      // Edit keeps the pack's saved industries; an empty list is never sent
      if (valuePackData.targetIndustries?.length) {
        formData.append(
          'targetIndustries',
          JSON.stringify(valuePackData.targetIndustries),
        );
      }

      // Tags for the custom storefront filters (Settings > Dynamic Filters)
      if (valuePackData.filterValues) {
        formData.append('filterValues', JSON.stringify(valuePackData.filterValues));
      }

      // Append files
      if (valuePackData.imageFile instanceof File) {
        formData.append('image', valuePackData.imageFile);
      }

      if (valuePackData.bannerImageFile instanceof File) {
        formData.append('bannerImage', valuePackData.bannerImageFile);
      }

      // Append dates
      if (valuePackData.publishedAt) {
        formData.append('publishedAt', valuePackData.publishedAt);
      }

      if (valuePackData.expiresAt) {
        formData.append('expiresAt', valuePackData.expiresAt);
      }

      const response = await Axios.post(
        `/valuepack/updateValuePack/${id}`,
        formData,
        {
          // headers: {
          'Content-Type': 'multipart/form-data',
          // },
        },
      );

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating value pack:', error);

      let errorMessage = 'Failed to update value pack';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  fetchAllValuePacks: async () => {
    try {
      const response = await Axios.fetch('/valuepack/getAllValuePacks');

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const valuePacks = response.data.data || response.data || [];

      if (!Array.isArray(valuePacks)) {
        console.warn('Value packs data is not an array:', valuePacks);
        throw new Error('Value packs data is not in expected format');
      }

      return valuePacks;
    } catch (error) {
      console.error('Error fetching value packs:', error);

      let errorMessage = 'Failed to fetch value packs';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  // Id and name of value packs for pickers, one page at a time (much lighter
  // than fetchAllValuePacks, which loads every tier).
  // {page, limit, search} → a page; {ids: [...]} → just those packs.
  // Returns {valuePacks, hasMore}.
  fetchValuePackOptions: async ({page = 1, limit = 20, search = '', ids} = {}) => {
    const params = new URLSearchParams(
      ids?.length
        ? {ids: ids.join(',')}
        : {page: String(page), limit: String(limit), search},
    );
    const response = await Axios.fetch(`/valuepack/options?${params}`);
    const data = response?.data?.data;
    // A server without this endpoint answers with the site's HTML, not JSON
    if (!Array.isArray(data?.valuePacks)) {
      throw new Error('Value pack list is not available on this server');
    }
    return {valuePacks: data.valuePacks, hasMore: !!data.hasMore};
  },

  fetchValuePackById: async id => {
    try {
      if (!id) {
        throw new Error('Value pack ID is required');
      }

      const response = await Axios.fetch(`/valuepack/getValuePackById/${id}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const responseData = response.data.data || response.data;
      // Backend nests the actual value pack under `valuePack` (alongside `meta`);
      // fall back to the raw object for any older/flat response shape.
      const valuePack = responseData?.valuePack || responseData;

      if (!valuePack) {
        throw new Error('Value pack not found');
      }

      return valuePack;
    } catch (error) {
      console.error('Error fetching value pack:', error);

      let errorMessage = 'Failed to fetch value pack';

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Value pack not found';
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  deleteValuePack: async (id, label = '') => {
    try {
      if (!id) {
        throw new Error('Value pack ID is required');
      }

      const response = await Axios.delete(
        `/valuepack/deleteValuePack/${id}?label=${encodeURIComponent(label)}`,
      );

      return response.data;
    } catch (error) {
      console.error('Error deleting value pack:', error);

      let errorMessage = 'Failed to delete value pack';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  transformTierPricingData: tier => {
    if (tier.originalPrice !== undefined) {
      let calculatedCurrentPrice = tier.originalPrice;

      if (tier.discountType && tier.discountValue && tier.discountValue > 0) {
        if (tier.discountType === 'flat') {
          calculatedCurrentPrice = Math.max(
            0,
            tier.originalPrice - tier.discountValue,
          );
        } else if (tier.discountType === 'percentage') {
          calculatedCurrentPrice =
            tier.originalPrice -
            (tier.originalPrice * tier.discountValue) / 100;
          calculatedCurrentPrice = Number(calculatedCurrentPrice.toFixed(2));
        }
      }

      // Extract durationId from object if needed
      let durationId = tier.durationId || tier.duration;
      if (durationId && typeof durationId === 'object' && durationId.id) {
        durationId = durationId.id;
      }

      // Extract countryId from object if needed
      let countryId = tier.countryId || tier.countryCode;
      if (countryId && typeof countryId === 'object' && countryId.id) {
        countryId = countryId.id;
      }

      return {
        ...tier,
        durationId: durationId,
        countryId: countryId,
        originalPrice: Number(tier.originalPrice.toFixed(2)),
        currentPrice: Number(
          (tier.currentPrice || calculatedCurrentPrice).toFixed(2),
        ),
        discountType: tier.discountType || null,
        discountValue: tier.discountValue || null,
        basePrice: undefined,
        // Remove old field names if they exist
        // duration: undefined,
        // countryCode: undefined
      };
    }

    if (tier.basePrice !== undefined) {
      let originalPrice = tier.basePrice;
      let currentPrice = tier.basePrice;

      if (tier.discountType && tier.discountValue && tier.discountValue > 0) {
        if (tier.discountType === 'flat') {
          currentPrice = Math.max(0, tier.basePrice - tier.discountValue);
        } else if (tier.discountType === 'percentage') {
          currentPrice =
            tier.basePrice - (tier.basePrice * tier.discountValue) / 100;
          currentPrice = Number(currentPrice.toFixed(2));
        }
      }

      // Extract durationId from object if needed
      let durationId = tier.durationId || tier.duration;
      if (durationId && typeof durationId === 'object' && durationId.id) {
        durationId = durationId.id;
      }

      // Extract countryId from object if needed
      let countryId = tier.countryId || tier.countryCode;
      if (countryId && typeof countryId === 'object' && countryId.id) {
        countryId = countryId.id;
      }

      return {
        ...tier,
        durationId: durationId, // CHANGED: Send durationId to backend
        countryId: countryId, // CHANGED: Send countryId to backend
        originalPrice: Number(originalPrice.toFixed(2)),
        currentPrice: Number(currentPrice.toFixed(2)),
        discountType: tier.discountType || null,
        discountValue: tier.discountValue || null,
        basePrice: undefined,
        // Remove old field names if they exist
        duration: undefined,
        countryCode: undefined,
      };
    }

    throw new Error('Tier data must contain either originalPrice or basePrice');
  },

  saveValuePackTiers: async (id, tiersData) => {
    try {
      if (!id) {
        throw new Error('Value pack ID is required');
      }

      if (!Array.isArray(tiersData) || tiersData.length === 0) {
        throw new Error('Tiers data must be a non-empty array');
      }

      const transformedTiersData = tiersData.map((tier, index) => {
        try {
          return valuePackApi.transformTierPricingData(tier);
        } catch (error) {
          throw new Error(
            `Error transforming tier at index ${index}: ${error.message}`,
          );
        }
      });

      const response = await Axios.post(`/valuepack/saveValuePackTiers/${id}`, {
        tiersData: transformedTiersData,
      });

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error saving value pack tiers:', error);

      let errorMessage = 'Failed to save value pack tiers';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  updateValuePackTiers: async (id, tiers) => {
    try {
      if (!id) {
        throw new Error('Value pack ID is required');
      }

      if (!Array.isArray(tiers) || tiers.length === 0) {
        throw new Error('Tiers data must be a non-empty array');
      }

      const transformedTiers = tiers.map((tier, index) => {
        try {
          return valuePackApi.transformTierPricingData(tier);
        } catch (error) {
          throw new Error(
            `Error transforming tier at index ${index}: ${error.message}`,
          );
        }
      });

      const response = await Axios.post(`/valuepack/updateTiers/${id}`, {
        tiersData: transformedTiers,
      });

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating tiers:', error);

      let errorMessage = 'Failed to update tiers';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getValuePackTiers: async id => {
    try {
      if (!id) {
        throw new Error('Value pack ID is required');
      }

      const response = await Axios.fetch(`/valuepack/getValuePackTiers/${id}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const tiers = response.data.data || response.data;

      return tiers;
    } catch (error) {
      console.error('Error fetching value pack tiers:', error);

      let errorMessage = 'Failed to fetch value pack tiers';

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Value pack tiers not found';
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  deleteValuePackTier: async (valuePackId, tierId) => {
    try {
      if (!valuePackId || !tierId) {
        throw new Error('Value pack ID and tier ID are required');
      }

      const response = await Axios.delete(
        `/valuepack/deleteValuePackTier/${valuePackId}/${tierId}`,
      );

      return response.data;
    } catch (error) {
      console.error('Error deleting value pack tier:', error);

      let errorMessage = 'Failed to delete value pack tier';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  deleteValuePackTiersByCriteria: async (valuePackId, criteria) => {
    try {
      if (!valuePackId || !criteria.duration || !criteria.countryCode) {
        throw new Error(
          'Value pack ID, duration, and country code are required',
        );
      }

      const response = await Axios.delete(
        `/valuepack/deleteValuePackTiersByCriteria/${valuePackId}`,
        {
          data: criteria,
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error deleting tiers by criteria:', error);

      let errorMessage = 'Failed to delete tiers';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  fetchCommonData: async () => {
    try {
      const response = await Axios.fetch('/valuepack/common-settings');

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const commonData = response.data.data || response.data || {};

      return commonData;
    } catch (error) {
      console.error('Error fetching common data:', error);

      let errorMessage = 'Failed to fetch common data';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  updateCommonData: async commonData => {
    try {
      const response = await Axios.post(
        '/valuepack/common-settings',
        commonData,
      );

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const updatedData = response.data.data || response.data;

      return updatedData;
    } catch (error) {
      console.error('Error updating common data:', error);

      let errorMessage = 'Failed to update common data';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },
};
